// Import Libraries
import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import cloneDeep from 'lodash.clonedeep';
import { builderDispatch, WebUI, ShelfUI } from '../_utils/ui';
import Spinner from '../_utils/spinner';
import Breadcrumb from '../_utils/breadcrumb';
import Paginator from '../_utils/paginator';
import setCanonicalUrl from '../_utils/tools/canonical-url';
import Events from './flux-events';
import NUps from './n-ups';
import MobileFilters from './mobile-filters';
import Products from './products';
import NoResults from './no-results';
import MobileFiltersFullScreen from './mobile-filters-full-screen';
import generateQueryString from './generate-query-string';
import Filters from './filters';
import FavouritedFilters from './favourited_filters';
import Modal from './modal';

// Shelf Category
class ShelfCategorySearch extends React.Component {
  constructor(props) {
    super(props);

    const { start_state: startState = {} } = cloneDeep(this.props);

    this.state = {
      isClient: false,
      data: this.manipulateData(this.props),
      sort: startState.sort || 'best-sellers',
      page: startState.page || 1,
      filters: startState.filters || {},
      delivery_options: startState.delivery || [],
      search: startState.search || '',
      showMobileFiltersFullScreen: false,
      favourites: [],
      loggedOutFavourites: [],
      favouritesFiltered: startState.favourited,
      hideModalFilters: true,
      filterCount: this.updateFilterCount(
        startState.filters,
        startState.delivery
      ),
      loggedIn: (() => {
        try {
          return typeof App !== 'undefined' &&
            App.order &&
            App.order.logged_in();
        } catch (e) {
          return false;
        }
      })(),
    };

    this.handleGoToPage = this.handleGoToPage.bind(this);
    this.handlePageNext = this.handlePageNext.bind(this);
    this.handlePagePrevious = this.handlePagePrevious.bind(this);
    this.handleFavouritesFilterClick = this.handleFavouritesFilterClick.bind(
      this
    );
    this.handleFilterOptionSelected = this.handleFilterOptionSelected.bind(
      this
    );
    this.handleFiltersReplace = this.handleFiltersReplace.bind(this);
    this.handleClearAllFilters = this.handleClearAllFilters.bind(this);
    this.handleDeliveryOptionSelected = this.handleDeliveryOptionSelected.bind(
      this
    );
    this.handleSortChange = this.handleSortChange.bind(this);
    this.handleSearchChange = this.handleSearchChange.bind(this);
    this.handleMobileFiltersShow = this.handleMobileFiltersShow.bind(this);
    this.handleMobileFiltersHide = this.handleMobileFiltersHide.bind(this);
    this.handleFavourited = this.handleFavourited.bind(this);
    this.handleFavouritesLoad = this.handleFavouritesLoad.bind(this);
    this.handleModalFiltersShow = this.handleModalFiltersShow.bind(this);
    this.handleModalFiltersHide = this.handleModalFiltersHide.bind(this);
    this.handleFavouritesLoad();
  }
  componentDidMount() {
    this.setState({ isClient: true });
    this.builderDispatchToken = builderDispatch.register(payload => {
      switch (payload.type) {
        case WebUI.LOGIN:
          if (!payload.previous_basket_contents) {
            this.handleFavouritesLoad();
          }
          break;
        case WebUI.CART_MERGED:
          this.handleFavouritesLoad();
          break;
        case Events.FAVOURITED:
          this.handleFavourited(payload.designCode);
          break;
        case Events.LOGGED_OUT_FAVOURITED:
          this.handleLoggedOutFavourited(payload.designCode);
          break;
        case ShelfUI.FILTER_SELECTED:
          this.handleFilterOptionSelected(
            payload.filter,
            payload.option,
            payload.multiselectable
          );
          break;
        case ShelfUI.DELIVERY_OPTION_SELECTED:
          this.handleDeliveryOptionSelected(payload.options);
          break;
        default:
      }
    });

    this.previousQueryString = generateQueryString(this.state);
    const { canonical_url_parameters: canonicalUrlParameters } = this.props;

    setCanonicalUrl(window, canonicalUrlParameters);

    window.addEventListener('popstate', this.reloadPage);
  }
  UNSAFE_componentWillUpdate(nextProps, nextState) {
    this.loadData(nextState);
  }

  componentDidUpdate() {
    if (typeof window === 'undefined') return;

    if (window.history) {
      const { noUrlRewrite } = this.props;
      (function fn() {
        if (noUrlRewrite || this.noUrlRewriteThisTime) {
          this.noUrlRewriteThisTime = false;
          return;
        }

        const {
          filters,
          search,
          delivery_options: deliveryOptions,
          sort,
          page,
          favouritesFiltered,
        } = this.state;
        const params = {};

        let paramsString = '';

        for (const key in filters) {
          if (filters[key].length === 0) continue;

          params[key] = filters[key].map(f => encodeURIComponent(f)).join('|');
        }

        if (search) params.search = encodeURIComponent(search);

        if (deliveryOptions && deliveryOptions.length) {
          params['delivery-options'] = deliveryOptions.join(
            encodeURIComponent('|')
          );
        }

        if (sort !== 'best-sellers') params._sort = sort; // eslint-disable-line no-underscore-dangle

        if (favouritesFiltered) params.favourited = 'true';

        if (page !== 1) params._page = page; // eslint-disable-line no-underscore-dangle

        for (const key in params) {
          if (paramsString.length) {
            paramsString += '&';
          }

          paramsString += `${encodeURIComponent(key)}=${params[key]}`;
        }

        if (this.lastPushedHistory === paramsString) return;

        this.lastPushedHistory = paramsString;

        if (paramsString) {
          paramsString = `?${paramsString}`;
        }

        const historyState = {
          filters: cloneDeep(filters),
          search,
          delivery_options: deliveryOptions,
          sort,
          favouritesFiltered,
          page,
        };

        const stateUrl =
          window.location.origin + window.location.pathname + paramsString;

        if (!this._urlHistoryInitialized) {
          this._urlHistoryInitialized = true;
          if (window.history && typeof window.history.replaceState === 'function') {
            window.history.replaceState(historyState, '', stateUrl);
          } else {
            window.history.pushState(historyState, '', stateUrl);
          }
        } else {
          window.history.pushState(historyState, '', stateUrl);
        }
      }.call(this));
    }
    const { canonical_url_parameters: canonicalUrlParameters } = this.props;

    setCanonicalUrl(window, canonicalUrlParameters);
  }

  componentWillUnmount() {
    window.removeEventListener('popstate', this.reloadPage);
  }

  reloadPage() {
    window.location.reload();
  }

  handleGoToPage(page) {
    this.setState({
      page,
    });
  }

  handlePageNext() {
    this.setState(({ page }) => ({
      page: page + 1,
    }));
  }

  handlePagePrevious() {
    const { page } = this.state;
    if (page > 1) {
      this.setState({
        page: page - 1,
      });
    }
  }

  handleFavouritesFilterClick() {
    this.setState(state => {
      return {
        favouritesFiltered: !state.favouritesFiltered,
        page: 1,
      };
    });
  }

  handleFilterOptionSelected(filter, option, multiFlag) {
    let multiselectable = multiFlag;
    if (typeof multiselectable === 'undefined') {
      multiselectable = true;
    }

    const { filters: updatedFilters, delivery_options } = this.state;
    updatedFilters[filter] = updatedFilters[filter]
      ? updatedFilters[filter]
      : [];

    if (option) {
      // remove or add
      const index = updatedFilters[filter].indexOf(option);
      if (index > -1) {
        updatedFilters[filter].splice(index, 1);
      } else {
        // if not multi selectable can only include one item at a time
        if (!multiselectable) updatedFilters[filter] = [];

        updatedFilters[filter].push(option);

        builderDispatch.delayedDispatch({
          type: Events.OMNI_FILTER_SELECTED,
          filter,
          option,
        });
      }
    } else {
      // clear all
      updatedFilters[filter] = [];
      builderDispatch.delayedDispatch({
        type: Events.OMNI_FILTER_CLEARED,
        filter,
        option,
      });
    }

    this.setState({
      filters: updatedFilters,
      page: 1,
      filterCount: this.updateFilterCount(updatedFilters, delivery_options),
    });
  }

  handleFiltersReplace(filters) {
    const { delivery_options, filters: prevStateFilters } = this.state;
    const isEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);
    if (isEqual(filters, prevStateFilters)) {
      this.setState({
        filters: {},
        page: 1,
        filterCount: this.updateFilterCount(filters, delivery_options),
      });
    } else {
      this.setState({
        filters: filters,
        page: 1,
        filterCount: this.updateFilterCount(filters, delivery_options),
      });
    }
  }

  handleClearAllFilters() {
    this.setState({
      filters: {},
      delivery_options: '',
      favouritesFiltered: false,
      search: '',
      page: 1,
      sort: 'best-sellers',
      filterCount: 0,
    });
  }

  handleDeliveryOptionSelected(options) {
    this.setState({
      delivery_options: options,
      page: 1,
      filterCount: this.updateFilterCount(this.state.filters, options),
    });

    const { data } = this.state;
    const selectedOptions = (options || []).sort().join('');
    const title = data.delivery_options.reduce((memo, option) => {
      return option.key === selectedOptions
        ? option.title
        : memo;
    }, 'Show all');

    builderDispatch.delayedDispatch({
      type: ShelfUI.FILTER_SELECTED,
      filter: 'Delivery Option',
      option: title,
    });
  }

  updateFilterCount(filters = [], delivery_options = []) {
    const {
      category: { filters: availableFilters },
    } = this.props;
    let count = 0;
    Object.entries(filters).map(([filter, arrayOfValues]) => {
      if (
        availableFilters
          .map(value => value.key.toLowerCase())
          .includes(filter.toLowerCase())
      ) {
        if (arrayOfValues.length > 0) {
          count += arrayOfValues.length;
        }
      }
    });
    if (delivery_options && delivery_options.length > 0) {
      count = count + delivery_options.length
    }
    return count;
  }

  handleSortChange(option) {
    this.setState({
      sort: option,
      page: 1,
    });
  }

  handleSearchChange(value) {
    const { search } = this.state;
    if (value === search) return;

    this.justSearched = true;

    this.setState({
      search: value,
      page: 1,
    });
  }

  handleMobileFiltersShow() {
    this.setState({ showMobileFiltersFullScreen: true });
  }

  handleMobileFiltersHide() {
    this.setState({ showMobileFiltersFullScreen: false });
  }

  handleModalFiltersShow() {
    this.setState({ hideModalFilters: false });
  }

  handleModalFiltersHide() {
    this.setState({ hideModalFilters: true });
  }

  handleLoggedOutFavourited(designCode) {
    const { loggedOutFavourites } = this.state;
    const index = loggedOutFavourites.indexOf(designCode);

    if (index === -1) {
      loggedOutFavourites.push(designCode);
    } else {
      loggedOutFavourites.splice(index, 1);
    }

    this.setState({ loggedOutFavourites });
  }
  handleFavourited(designCode) {
    const { favourites } = this.state;
    const index = favourites.indexOf(designCode);

    if (index === -1) {
      favourites.push(designCode);
    } else {
      favourites.splice(index, 1);
    }

    this.setState({ favourites });
  }

  async handleFavouritesLoad() {
    if (typeof App === 'undefined' || !App.order.logged_in()) return;
    await this.updateLoggedOutFavourites();

    fetch('/favourites', {
      credentials: 'same-origin',
    })
      .then(response => response.json())
      .then(favourites => {
        this.setState({
          favourites,
          loggedIn: typeof App !== 'undefined' && App.order.logged_in(),
        });
      })
      .catch(() => { });
  }

  updateLoggedOutFavourites = async () => {
    const { loggedOutFavourites } = this.state;
    for (const designCode of loggedOutFavourites) {
      const data = new FormData();
      data.append('design_code', designCode);
      data.append('checked', true);

      await fetch('/favourites', {
        method: 'PUT',
        credentials: 'same-origin',
        body: data,
      });
    };
  }

  manipulateData(props) {
    const { data: stateData } = this.state || {};
    const data = cloneDeep(props.category || stateData);
    const {
      products: { products, total },
    } = props;

    function normalizeDeliveryOptions(options) {
      return (options || []).map(option => ({
        title: option.mobile_title,
        key: option.key
      }));
    }

    if (props.category) {
      data.delivery_options = normalizeDeliveryOptions(data.delivery_options);
    }

    data.products_total = total;
    data.products = products;
    data.facets = props.facets;

    return data;
  }

  loadData(state) {
    const queryString = generateQueryString(state);

    if (this.previousQueryString === queryString) {
      return;
    }
    const { data } = this.state;

    this.setState({ loadingContent: true }, () => {
      const url = `/prism_category/${data.path}/data.json?${queryString}`;

      $.get(url, responseData => {
        this.setState(
          {
            data: this.manipulateData(responseData),
            loadingContent: false,
          },
          () => {
            ReactDOM.findDOMNode(this).scrollIntoView();
          }
        );
      });
    });

    this.previousQueryString = queryString;
  }

  roundOptionActive() {
    let foundActiveRoundOption = false;
    let roundedCornersOption;
    let filter;

    const { filters, data } = this.state;

    for (const selectedFilterKey in filters) {
      // The option get activated if is the only option active for the filter.
      // No worth to even try to calculate it if more than one option
      // in the filter is active
      if (filters[selectedFilterKey].length !== 1) {
        continue;
      }

      roundedCornersOption = null;

      for (const index in data.filters) {
        filter = data.filters[index];

        if (
          filter.key === selectedFilterKey &&
          !!filter.rounded_corners_option
        ) {
          roundedCornersOption = filter.rounded_corners_option;
          break;
        }
      }

      if (
        !!roundedCornersOption &&
        filters[selectedFilterKey][0] === roundedCornersOption
      ) {
        foundActiveRoundOption = true;
        break;
      }
    }

    return foundActiveRoundOption;
  }

  shouldShowNups() {
    const { data, filters } = this.state;
    if (!data.n_ups || data.n_ups.length === 0) {
      return false;
    }


    return true;
  }

  allCategoryTitles() {
    const categoryTitles = ((this.data || {}).products || []).reduce(
      (memo, product) => {
        const updatedMemo = { ...memo };
        updatedMemo[product.category_title] = 1;
        return updatedMemo;
      },
      {}
    );

    return Object.keys(categoryTitles).sort();
  }

  render() {
    let totalPageCount;
    let $paginator;
    let $productPreviews;
    let $spinner;
    let $favouritedFilter = '';

    const {
      loadingContent,
      showMobileFiltersFullScreen,
      data,
      favourites,
      filters,
      page,
      favouritesFiltered,
      search,
      delivery_options: deliveryOptions,
      sort,
      loggedIn
    } = this.state;

    const {
      preview_in_modal: previewInModal,
      category,
      products_per_page: productsPerPage,
      show_thumbnail_images: showThumbnailImages,
    } = this.props;

    if (loadingContent && !showMobileFiltersFullScreen) {
      $spinner = <Spinner />;
    }

    if (!loadingContent) {
      if (data.products.length && data.delivery_options.length > 0) {
        $productPreviews = (
          <Products
            products={data.products}
            roundOption={this.roundOptionActive()}
            favourites={favourites}
            preSelections={filters}
            previewInModal={previewInModal}
            deliveryOptions={category.delivery_options}
            showThumbnailImages={showThumbnailImages}
            selectedDeliveryOptions={(deliveryOptions || []).join('|')}
            loggedIn={loggedIn}
          />
        );
      } else {
        $productPreviews = (
          <NoResults onClearAllFilters={this.handleClearAllFilters} />
        );
      }

      totalPageCount = Math.ceil(data.products_total / productsPerPage);

      if (totalPageCount > 1 && data.delivery_options.length > 0) {
        $paginator = (
          <Paginator
            page={parseInt(page)}
            totalPageCount={totalPageCount}
            scrollTarget="#main"
            onPageNext={this.handlePageNext}
            onPagePrevious={this.handlePagePrevious}
            onGoToPage={this.handleGoToPage}
          />
        );
      }

      if (
        typeof App !== 'undefined' &&
        App.order.logged_in() &&
        this.state.isClient
      ) {
        $favouritedFilter = (
          <FavouritedFilters
            handleFavouritesFilterCheck={this.handleFavouritesFilterClick}
            favouritesFiltered={favouritesFiltered}
          />
        );
      }

      if (this.justSearched) {
        this.justSearched = false;

        builderDispatch.dispatch({
          type: search ? Events.SEARCH_SELECTED : Events.SEARCH_CLEARED,
          option: search,
          succeeded: data.products.length > 0,
        });
      }
    }

    const shouldShowNups = this.shouldShowNups();

    return (
      <div className="product-shelf-search">
        {$spinner}
        <div>
          <div className="row">
            <div className="col">
              <Breadcrumb segments={data.breadcrumbs} />
            </div>
          </div>

          <div
            className="d-none d-md-block category-description top-border"
            dangerouslySetInnerHTML={{ __html: data.description }}
          />

          <div className={classNames({ 'd-none': !shouldShowNups })}>
            <NUps
              collection={data.n_ups}
              onUrlFiltersChange={this.handleFiltersReplace}
            />
          </div>

          <Filters
            filters={data.filters.slice(0, 3)}
            facets={data.facets}
            category={data.title}
            selected_filters={filters}
            delivery_options={data.delivery_options}
            selected_delivery_options={deliveryOptions}
            default_search_term={search}
            onFilterOptionSelected={this.handleFilterOptionSelected}
            hideFilterModal={this.handleModalFiltersShow}
            onSearchChange={this.handleSearchChange}
            totalProducts={data.products_total}
            showingProducts={data.products.length}
            handleSortChange={this.handleSortChange}
            favouriteFilters={$favouritedFilter}
            onClearAllFilters={this.handleClearAllFilters}
            selectedFiltersCount={this.state.filterCount}
            sortBy={sort}
          />
          {$productPreviews}

          {$paginator}
          {data.secondary_description.length > 0 && (
            <div
              className="category-description"
              dangerouslySetInnerHTML={{ __html: data.secondary_description }}
            />
          )}
        </div>

        <div
          className={classNames(
            showMobileFiltersFullScreen ? 'd-block' : 'd-none'
          )}>
          <MobileFiltersFullScreen
            showMobileFiltersFullScreen={showMobileFiltersFullScreen}
            onDoneClick={this.handleMobileFiltersHide}
            onClearAllFilters={this.handleClearAllFilters}>
            <MobileFilters
              filters={data.filters}
              facets={data.facets}
              selected_filters={filters}
              delivery_options={data.delivery_options}
              selected_delivery_options={deliveryOptions}
              sortValue={sort}
              onFilterOptionSelected={this.handleFilterOptionSelected}
              onSortChange={this.handleSortChange}
            />
          </MobileFiltersFullScreen>
        </div>
        <Modal
          delivery_options={data.delivery_options}
          facets={data.facets}
          filters={data.filters}
          handleModalFiltersHide={this.handleModalFiltersHide}
          hideModalFilters={this.state.hideModalFilters}
          onClearAllFilters={this.handleClearAllFilters}
          onDeliveryOptionSelected={this.handleDeliveryOptionSelected}
          onFilterOptionSelected={this.handleFilterOptionSelected}
          onSortChange={this.handleSortChange}
          selectedDeliveryOptions={deliveryOptions}
          selectedFilterCount={this.state.filterCount}
          selected_filters={filters}
          sortValue={sort}
        />
      </div>
    );
  }
}

ShelfCategorySearch.displayName = 'Shelf';

ShelfCategorySearch.propTypes = {
  noUrlRewrite: PropTypes.bool,
  preview_in_modal: PropTypes.bool.isRequired,
  category: PropTypes.shape({}),
  canonical_url_parameters: PropTypes.arrayOf(PropTypes.string),
  show_thumbnail_images: PropTypes.bool,
};

ShelfCategorySearch.defaultProps = {
  noUrlRewrite: null,
  category: null,
  canonical_url_parameters: [],
  show_thumbnail_images: null,
};

export default ShelfCategorySearch;
